import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MATTRACKERAPIConstants } from '../constants/constants';
import { SubstationLookup, UserLookup } from '../models';
import { StorageHub, LookupData } from './hub.service';

// ─── Cached-response shapes ──────────────────────────────────────────────────

/**
 * Parsed shape of the /getLookupData response (substations + users).
 * `raw` preserves the original response in case a component needs other fields.
 */
export interface LookupCache {
  substations:  SubstationLookup[];
  allUsers:     UserLookup[];
  pmUsers:      UserLookup[];
  plannerUsers: UserLookup[];
  raw: any;
}

// ─── SharedDataService ────────────────────────────────────────────────────────

/**
 * SharedDataService — single source of truth for all cacheable API data.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  WHAT IS CACHED (read-only, session-stable)                             │
 * │  ─────────────────────────────────────────────────────────────────────  │
 * │  • getLookupData()          → substations + users lists                 │
 * │  • getStorageHubs()         → hub list used in Admin tab                │
 * │  • getUsers()               → full user list used in Users tab          │
 * │  • getHubLocationTypes()    → 'Location_Type' lookup (Add Hub modal)    │
 * │  • getMatRequest(id)        → individual request data                   │
 * │  • getAttachments(id)       → attachment list for a request             │
 * │                                                                         │
 * │  WHAT IS NOT CACHED (mutating calls — handled in their own services)    │
 * │  ─────────────────────────────────────────────────────────────────────  │
 * │  saveMatRequest, uploadFile, deleteAttachment,                          │
 * │  addUser, updateUser, addStorageHub, updateStorageHub                   │
 * │  → each of these calls the relevant clearXxxCache() after success       │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * How caching works
 * -----------------
 * Each cache slot is an `Observable<T> | null`.
 *   null  → not yet fetched; first subscriber triggers the HTTP call
 *   Observable → shareReplay(1) replays the last result to new subscribers
 *                instantly, with zero extra HTTP requests
 *
 * Invalidation
 * ------------
 * After any mutating operation call the matching clear method:
 *   clearLookupCache()       after add/update user
 *   clearStorageHubCache()   after add/update hub
 *   clearUsersCache()        after add/update user
 *   clearMatRequestCache(id) after saving a request
 *   clearAttachmentsCache(id) after upload/delete attachment
 */
@Injectable({ providedIn: 'root' })
export class SharedDataService {

  private readonly baseUrl = `${environment.apiBaseUrl}`;

  constructor(private http: HttpClient) {}

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. Lookup Data  (substations + users)   —   /getLookupData
  // ═══════════════════════════════════════════════════════════════════════════

  private lookupCache$: Observable<LookupCache> | null = null;

  /**
   * Returns the substations + users lookup data.
   * HTTP GET fires only once per session; subsequent callers get the cached result.
   */
  getLookupData(): Observable<LookupCache> {
    if (!this.lookupCache$) {
      this.lookupCache$ = this.http
        .get<any>(`${this.baseUrl}/${MATTRACKERAPIConstants.GETLOOKUPDATA}`)
        .pipe(
          map((response: any) => {
            const raw   = response?.data ?? response;
            const subs  : SubstationLookup[] = Array.isArray(raw?.Substations) ? raw.Substations : [];
            const users : UserLookup[]        = Array.isArray(raw?.Users)       ? raw.Users       : [];
            return {
              substations:  subs,
              allUsers:     users,
              pmUsers:      users.filter(u => u.UserRoleCd === 'PM'),
              plannerUsers: users.filter(u => u.UserRoleCd === 'Planner'),
              raw:          response,
            } as LookupCache;
          }),
          shareReplay(1)
        );
    }
    return this.lookupCache$;
  }

  /**
   * Returns the raw API response shape so existing component code keeps working.
   * (Used by NewRequestService.getLookupData() as a backward-compat wrapper.)
   */
  getLookupDataRaw(): Observable<any> {
    return this.getLookupData().pipe(map(c => c.raw));
  }

  /** Call after add/update user — next getLookupData() will re-fetch. */
  clearLookupCache(): void { this.lookupCache$ = null; }

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. Storage Hubs   —   /getStorageHubs
  // ═══════════════════════════════════════════════════════════════════════════

  private storageHubCache$: Observable<StorageHub[]> | null = null;

  /**
   * Returns the full list of storage hubs.
   * Replays the last result to new subscribers — no repeat HTTP calls on
   * every Admin tab open.
   */
  getStorageHubs(): Observable<StorageHub[]> {
    if (!this.storageHubCache$) {
      this.storageHubCache$ = this.http
        .get<any>(`${this.baseUrl}/${MATTRACKERAPIConstants.GET_STORAGE_HUBS}`)
        .pipe(
          map(response => (response?.data ?? []) as StorageHub[]),
          shareReplay(1)
        );
    }
    return this.storageHubCache$;
  }

  /** Call after add/update hub — next getStorageHubs() will re-fetch. */
  clearStorageHubCache(): void { this.storageHubCache$ = null; }

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. Users list   —   /getUsers
  // ═══════════════════════════════════════════════════════════════════════════

  private usersCache$: Observable<any> | null = null;

  /**
   * Returns the full user list.
   * Cached for the session — call clearUsersCache() after add/update.
   */
  getUsers(): Observable<any> {
    if (!this.usersCache$) {
      this.usersCache$ = this.http
        .get<any>(`${this.baseUrl}/${MATTRACKERAPIConstants.GET_USERS}`)
        .pipe(shareReplay(1));
    }
    return this.usersCache$;
  }

  /** Call after add/update/delete user — next getUsers() will re-fetch. */
  clearUsersCache(): void {
    this.usersCache$ = null;
    // Also clear the lookup cache since it contains user data too
    this.clearLookupCache();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. Hub Location Types   —   /getLookupData?codeSetName=Location_Type
  //    (used by the Add Hub modal — re-opens each time but data never changes)
  // ═══════════════════════════════════════════════════════════════════════════

  private hubLocationTypesCache$: Observable<LookupData[]> | null = null;

  /**
   * Returns the Location_Type reference codes used in the Add Hub modal.
   * Cached permanently — this data only changes via a deployment, not user action.
   */
  getHubLocationTypes(): Observable<LookupData[]> {
    if (!this.hubLocationTypesCache$) {
      this.hubLocationTypesCache$ = this.http
        .get<any>(`${this.baseUrl}/${MATTRACKERAPIConstants.GET_LOOKUP_DATA}?codeSetName=Location_Type`)
        .pipe(
          map(response => (response?.data ?? []) as LookupData[]),
          shareReplay(1)
        );
    }
    return this.hubLocationTypesCache$;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. Mat Request by ID   —   /getRequestById?requestId=xxx
  //    Per-ID cache — create-request and view-page both load the same ID
  // ═══════════════════════════════════════════════════════════════════════════

  private matRequestCache = new Map<string, Observable<any>>();

  /**
   * Returns the mat request data for the given ID.
   * First call fires the HTTP GET and caches the result.
   * Subsequent calls for the same ID return the cached observable instantly.
   *
   * IMPORTANT: call clearMatRequestCache(id) after every saveMatRequest() so
   * the next navigation re-fetches fresh data.
   */
  getMatRequest(requestId: string): Observable<any> {
    if (!this.matRequestCache.has(requestId)) {
      const req$ = this.http
        .get<any>(`${this.baseUrl}/getRequestById?requestId=${requestId}`)
        .pipe(shareReplay(1));
      this.matRequestCache.set(requestId, req$);
    }
    return this.matRequestCache.get(requestId)!;
  }

  /**
   * Invalidate the cached request for a specific ID.
   * Call this immediately after saveMatRequest() succeeds.
   */
  clearMatRequestCache(requestId: string): void {
    this.matRequestCache.delete(requestId);
  }

  /** Invalidate ALL cached requests (e.g. bulk operations). */
  clearAllMatRequestCaches(): void {
    this.matRequestCache.clear();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. Attachments by Request ID   —   /getAttachmentsByRequest?requestId=xxx
  //    Per-ID cache — must be invalidated after every upload or delete
  // ═══════════════════════════════════════════════════════════════════════════

  private attachmentsCache = new Map<string, Observable<any[]>>();

  /**
   * Returns the attachment list for the given request ID.
   * Cached per-ID — call clearAttachmentsCache(id) after upload or delete.
   */
  getAttachments(requestId: string, includeDeleted = false): Observable<any[]> {
    const key = `${requestId}:${includeDeleted}`;
    if (!this.attachmentsCache.has(key)) {
      const params: any = { requestId };
      if (includeDeleted) { params.includeDeleted = 'true'; }
      const att$ = this.http
        .get<any>(`${this.baseUrl}/${MATTRACKERAPIConstants.GET_ATTACHMENTS_BY_REQUEST}`, { params })
        .pipe(
          map(res => {
            // API may return a plain array or a wrapper like { Attachments: [...] }
            if (Array.isArray(res)) return res;
            if (res?.Attachments) return res.Attachments;
            if (res?.attachments) return res.attachments;
            return [];
          }),
          shareReplay(1)
        );
      this.attachmentsCache.set(key, att$);
    }
    return this.attachmentsCache.get(key)!;
  }

  /**
   * Invalidate the attachment cache for a specific request.
   * Call this after uploadFile() or deleteAttachment() succeeds.
   */
  clearAttachmentsCache(requestId: string): void {
    // Remove all keys that start with this requestId (covers includeDeleted variants)
    for (const key of this.attachmentsCache.keys()) {
      if (key.startsWith(`${requestId}:`)) {
        this.attachmentsCache.delete(key);
      }
    }
  }
}
